<template>
  <v-container fluid grid-list-lg>
    <v-layout row wrap class="mb-3">
      <v-btn round color="primary" @click.prevent="doBack">
        <v-icon left dark>mdi-chevron-left</v-icon>
        Back
      </v-btn>
    </v-layout>
    <v-layout row wrap>
      <v-flex xs12>
        <message :messages="messages"></message>
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-card-title>
            <div>
              <div class="headline">{{labels.insureds}}</div>
            </div>
            <v-spacer></v-spacer>
            <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
          </v-card-title>
          <v-data-table :headers="headers" :items="insureds" v-bind:search="search" :rows-per-page-items="itemsPerPage" v-bind:pagination.sync="pagination" item-key="id" :loading="loading" :no-data-text="noDataText" :filter="customSearch">
            <template slot="headerCell" slot-scope="props">
              <v-tooltip bottom>
                <span slot="activator">
                  {{ props.header.text }}
                </span>
                <span>
                  {{ props.header.text }}
                </span>
              </v-tooltip>
            </template>
            <template slot="items" slot-scope="props">
              <td>{{ props.item.name }}</td>
              <td class="text-xs-right">{{ props.item.nricPassport }}</td>
              <td class="text-xs-right">{{ formatDate(props.item.dob) }}</td>
              <td class="text-xs-right">{{ computedCoverages[props.index] }} </td>
              <td class="text-xs-right">
                <table-action :rowData="props" :review="!props.item.isApplicant" :remove="!props.item.isApplicant" @remove="doRemoveInsured" @review="doReviewInsured"></table-action>
              </td>
            </template>
            <template slot="footer">
              <td colspan="100%">
                <v-layout justify-end>
                  <div class="pr-3">
                    <v-icon :color="Number(insuredCount.adult) === Number(globalForm.noAdult) ? 'success':'error'">
                      {{Number(insuredCount.adult) === Number(globalForm.noAdult) ? 'mdi-check':'mdi-close'}}
                    </v-icon>
                    {{insuredCount.adult}} / {{globalForm.noAdult}} Adult(s)
                  </div>
                  <div class="pr-3">
                    <v-icon :color="Number(insuredCount.child) === Number(globalForm.noChild) ? 'success':'error'">
                      {{Number(insuredCount.child) === Number(globalForm.noChild) ? 'mdi-check':'mdi-close'}}
                    </v-icon>
                    {{insuredCount.child}} / {{globalForm.noChild}} Child(ren)
                  </div>
                </v-layout>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-layout row wrap class="mt-2">
      <v-btn color="secondary" @click.prevent="doAddInsured">
        Add Insured
        <v-icon right>mdi-account-plus</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-menu offset-y>
        <v-btn slot="activator" color="secondary" :loading="proceedLoading" :disabled="proceedLoading">
          More Options
          <v-icon right dark>mdi-dots-vertical</v-icon>
        </v-btn>
        <v-list>
          <v-list-tile @click="$refs.fileInput.click()">
            <v-list-tile-content>Upload Insureds</v-list-tile-content>
          </v-list-tile>
          <v-list-tile @click="downloadInsureds">
            <v-list-tile-content>Download Insureds</v-list-tile-content>
          </v-list-tile>
        </v-list>
        <form ref="uploadForm">
          <input type="file" accept=".csv" :multiple="false" :disabled="uploading" ref="fileInput" @change="uploadInsureds">
        </form>
      </v-menu>
    </v-layout>
    <transition name="slide-x-transition" mode="out-in">
      <v-layout row wrap class="mt-2" id="insuredPnl" v-if="showInsuredPnl">
        <v-flex xs12>
          <v-card>
            <v-card-title>
              <div>
                <div class="headline">{{labels.insured}}</div>
              </div>
            </v-card-title>
            <v-card-text>
              <v-container fluid grid-list-lg>
                <v-layout row wrap>
                  <v-flex lg6 xs12>
                    <v-text-field key="name" v-model="insured.name" :label="labels.name" :error-messages="errors.collect('name')" data-vv-name="name" :data-vv-as="labels.name" v-validate="'required|name|max:60'"></v-text-field>
                  </v-flex>
                  <v-flex lg6 xs12>
                    <v-text-field key="nricPassport" v-uppercase="insured.nricPassport" v-model="insured.nricPassport" :label="labels.nricPassport" :error-messages="errors.collect('nricPassport')" data-vv-name="nricPassport" :data-vv-as="labels.nricPassport" v-validate="'required|alpha_num|max:20'"></v-text-field>
                  </v-flex>
                  <v-flex lg6 xs12>
                    <cal-picker key="dob" v-model="insured.dob" :label="labels.dob" v-validate="dobValidation" :data-vv-as="labels.dob" data-vv-name="dob" :error-messages="errors.collect('dob')"></cal-picker>
                  </v-flex>
                  <v-flex lg6 xs12 v-if="globalForm.travelType === 'F'">
                    <v-autocomplete key="relationship" :items="relationshipList" v-model="insured.relationship" :label="labels.relationship" item-text="label" item-value="value" data-vv-name="relationship" :data-vv-as="labels.relationship" :error-messages="errors.collect('relationship')" v-validate="'required'"></v-autocomplete>
                  </v-flex>
                  <v-flex lg6 xs12>
                    <v-radio-group key="gender" v-model="insured.gender" row :label="labels.gender" :error-messages="errors.collect('gender')" data-vv-name="gender" :data-vv-as="labels.gender" v-validate="'required'">
                      <v-radio v-for="gender in genderList" :key="gender.value" :label="gender.label" :value="gender.value"></v-radio>
                    </v-radio-group>
                  </v-flex>
                  <v-flex lg6 xs12>
                    <v-checkbox key="isStudent" v-model="insured.isStudent" :label="labels.isStudent" :error-messages="errors.collect('isStudent')" data-vv-name="isStudent" :data-vv-as="labels.isStudent" hide-details></v-checkbox>
                  </v-flex>
                  <v-flex lg6 xs12>
                    <v-text-field box :label="labels.coverage" :value="isChild(insured.dob, insured.isStudent)? 'Child' : 'Adult'" readonly></v-text-field>
                  </v-flex>
                </v-layout>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn flat color="error" @click.prevent="doCancelInsured">
                    Cancel
                  </v-btn>
                  <v-btn flat color="primary" @click.prevent="doConfirmInsured">
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-container>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </transition>
    <v-layout row wrap class="mt-3">
      <v-btn round color="primary" @click.prevent="doBack">
        <v-icon left dark>mdi-chevron-left</v-icon>
        Back
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn round color="primary" type="submit" @click.prevent="doProceed" :disabled="proceedLoading">
        Next
        <v-icon right dark>mdi-chevron-right</v-icon>
      </v-btn>
    </v-layout>
  </v-container>
</template>

<script>
import { Validator } from 'vee-validate'
import { AGENCY_QUOTATION_LIFECYCLE, QUOTE_TRAVEL_INSURED_ADD, QUOTE_TRAVEL_INSURED_REMOVE, QUOTE_TRAVEL_INSURED_UPDATE } from '@/store/types'
import MessageMixin from '@/mixins/message'
import { SaveQuoteMixin } from '../../shared/saveQuoteMixin'
import * as TableConst from '@/mixins/table'
import isAfter from 'date-fns/isAfter'
import parse from 'date-fns/parse'
import { gender } from '@/const/underwriting'
import { relationship } from '@/const/underwriting/travel'
import { yearDiff, removeTime, formatDisplayDate, displayDateFormat } from '@/utils/date'
import { csvObj, downloadPrompt } from '@/utils/file'

export default {
  data() {
    return {
      labels: {
        insureds: 'Insureds',
        insured: 'Insured',
        name: 'Name as in NRIC/Passport*',
        nricPassport: 'NRIC/Passport*',
        dob: 'Date of Birth*',
        gender: 'Gender*',
        relationship: 'Relationship to Main Insured*',
        isStudent: 'Is full time student?',
        coverage: 'Coverage'
      },
      headers: [
        { text: 'Name', align: 'left', value: 'name' },
        { text: 'NRIC/Passport No.', align: 'right', value: 'nricPassport' },
        { text: 'Date of Birth', align: 'right', value: 'dob' },
        { text: 'Coverage', align: 'right', value: 'coverage' },
        TableConst.tableActionHeader
      ],
      insured: {
        name: undefined,
        nricPassport: undefined,
        dob: undefined,
        relationship: undefined,
        gender: undefined,
        isStudent: undefined,
        isApplicant: undefined
      },
      pagination: {
        // Assign sort field by default (if not it will sort the 1st field)
        sortBy: 'id',
        descending: false,
        page: 1,
        // Follow the 1st value of the above variable (itemsPerPage)
        rowsPerPage: 10
      },
      search: undefined,
      showInsuredPnl: false,
      selectedIndex: null,
      uploading: false,
      downloading: false
    }
  },
  computed: {
    globalForm() {
      return this.$store.state.agency.quotation.travelForm
    },
    insureds() {
      const result = this.globalForm.insureds.map((item, index) => ({
        id: index,
        ...item
      }))
      return result
    },
    selectedPlan() {
      return this.$store.getters.travelQuoteSelectedPlan
    },
    dobValidation() {
      if (this.selectedPlan && (this.selectedPlan.minInsdAge !== null || typeof this.selectedPlan.minInsdAge !== 'undefined') && this.selectedPlan.maxInsdAge) {
        return { required: true, bday_range: [this.$store.state.agency.quotation.commonForm.startDate, this.selectedPlan.minInsdAge, this.selectedPlan.maxInsdAge, true] }
      } else {
        // hardCode is cannot find age
        return { required: true, bday_range: [this.$store.state.agency.quotation.commonForm.startDate, 1, 100] }
      }
    },
    genderList() {
      let result = []
      Object.keys(gender).forEach(function (key) {
        const status = gender[key]
        result.push({
          label: status,
          value: key
        })
      })
      return result
    },
    relationshipList() {
      let result = []
      Object.keys(relationship).forEach(function (key) {
        const status = relationship[key]
        result.push({
          label: status,
          value: key
        })
      })
      return result
    },
    insuredCount() {
      if (this.computedCoverages && this.computedCoverages.length > 0) {
        let adult = 0
        let child = 0
        for (const coverage of this.computedCoverages) {
          if (coverage === 'Child') {
            child++
          } else {
            adult++
          }
        }
        return {
          adult,
          child
        }
      } else {
        return {
          adult: 0,
          child: 0
        }
      }
    },
    computedCoverages() {
      // If we use isChild in the table display
      // It will rerun the method on every component update (causes lag if there are many records)
      if (this.insureds && this.insureds.length > 0) {
        let result = []
        for (const insured of this.insureds) {
          if (this.isChild(insured.dob, insured.isStudent)) {
            result.push('Child')
          } else {
            result.push('Adult')
          }
        }
        return result
      }
      return []
    },
    proceedLoading() {
      if (this.uploading || this.downloading) {
        return true
      } else {
        return false
      }
    }
  },
  methods: {
    doInit() {
      // Default value from vuex store 1st
    },
    isChild(dob, isStudent) {
      if (this.selectedPlan) {
        let maxAge
        if (isStudent) {
          maxAge = this.selectedPlan.maxStudentAge
        } else {
          maxAge = this.selectedPlan.maxChildAge
        }
        let age = yearDiff(this.$store.state.agency.quotation.commonForm.startDate, dob)
        if (!isAfter(removeTime(dob), removeTime(this.$store.state.agency.quotation.commonForm.startDate))) {
          age++
        }
        if (maxAge) {
          if (age <= maxAge) {
            return true
          }
        }
      }
      return false
    },
    doInitInsured() {
      this.insured.name = null
      this.insured.nricPassport = null
      this.insured.dob = null
      this.insured.relationship = null
      this.insured.gender = null
      this.insured.isStudent = false
      this.insured.isApplicant = false
      this.selectedIndex = null
    },
    doAddInsured() {
      this.doInitInsured()
      this.showInsuredPnl = true
      this.$nextTick(() => {
        this.$vuetify.goTo('#insuredPnl')
      })
    },
    doRemoveInsured(insured) {
      this.$store.commit(QUOTE_TRAVEL_INSURED_REMOVE, insured.item.id)
    },
    doReviewInsured(insured) {
      const { item } = insured
      this.insured.name = item.name
      this.insured.nricPassport = item.nricPassport
      this.insured.dob = item.dob
      this.insured.relationship = item.relationship
      this.insured.gender = item.gender
      this.insured.isStudent = item.isStudent
      this.insured.isApplicant = item.isApplicant

      this.selectedIndex = insured.index
      this.showInsuredPnl = true
      this.$nextTick(() => {
        this.$vuetify.goTo('#insuredPnl')
      })
    },
    doConfirmInsured() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.selectedIndex === null) {
            this.$store.commit(QUOTE_TRAVEL_INSURED_ADD, this.insured)
          } else {
            this.$store.commit(QUOTE_TRAVEL_INSURED_UPDATE, {
              index: this.selectedIndex,
              insured: this.insured
            })
          }
          this.showInsuredPnl = false
          this.doInitInsured()
        } else {
          this.promptFieldCheck()
        }
      })
    },
    doCancelInsured() {
      this.showInsuredPnl = false
      this.doInitInsured()
    },
    uploadInsureds(e) {
      this.resetMessages()
      if (e.target.files[0]) {
        this.uploading = true
        const file = e.target.files[0]
        // As edge dont support "accept='.csv'", we have to check file ext
        if (file.name.indexOf('.csv') !== -1) {
          const fileReader = new FileReader()
          fileReader.onload = async () => {
            const result = csvObj(fileReader.result)
            if (result && result.length > 0) {
              const validator = new Validator()
              let fullValid = true
              let commitInsureds = []
              for (let i = 0; i < result.length; i++) {
                const row = result[i]
                let noCol = 5
                if (this.globalForm.travelType === 'F') {
                  noCol = 6
                }
                let rowValid = true
                let errorMsg = null
                let commitInsured = {
                  name: undefined,
                  nricPassport: undefined,
                  dob: undefined,
                  relationship: undefined,
                  gender: undefined,
                  isStudent: false,
                  isApplicant: false
                }
                if (noCol === row.length) {
                  for (let x = 0; x < row.length; x++) {
                    let col = row[x].trim()
                    try {
                      switch (x) {
                        case 0:
                          // Name
                          const nameValidation = await validator.verify(col, 'required|name|max:60')
                          if (!nameValidation.valid) {
                            rowValid = false
                            errorMsg = nameValidation.errors[0].replace('{field}', 'Name')
                          } else {
                            commitInsured.name = col
                          }
                          break
                        case 1:
                          // NricPassport
                          col = col.toUpperCase()
                          const nricPassportValidation = await validator.verify(col, 'required|entry|max:20')
                          if (!nricPassportValidation.valid) {
                            rowValid = false
                            errorMsg = nricPassportValidation.errors[0].replace('{field}', 'Nric/Passport')
                          } else {
                            commitInsured.nricPassport = col
                          }
                          break
                        case 2:
                          // DoB
                          const convertVal = parse(col, displayDateFormat, new Date())
                          const dobValidation = await validator.verify(convertVal, this.dobValidation)
                          if (!dobValidation.valid) {
                            rowValid = false
                            errorMsg = dobValidation.errors[0].replace('{field}', 'Date of Birth')
                          } else {
                            commitInsured.dob = convertVal
                          }
                          break
                        case 3:
                          // Gender
                          col = col.toUpperCase()
                          const genderValidation = await validator.verify(col, 'required')
                          if (!genderValidation.valid) {
                            rowValid = false
                            errorMsg = genderValidation.errors[0].replace('{field}', 'Gender')
                          } else {
                            // Check valid values
                            if (col === 'F' || col === 'M') {
                              commitInsured.gender = col
                            } else {
                              rowValid = false
                              errorMsg = 'The Gender field must be either F/M.'
                            }
                          }
                          break
                        case 4:
                          // is Student
                          col = col.toUpperCase()
                          const studentValidation = await validator.verify(col, 'required')
                          if (!studentValidation.valid) {
                            rowValid = false
                            errorMsg = studentValidation.errors[0].replace('{field}', 'Is Student')
                          } else {
                            // Check valid values
                            if (col === 'Y' || col === 'N') {
                              if (col === 'Y') {
                                commitInsured.isStudent = true
                              } else {
                                commitInsured.isStudent = false
                              }
                            } else {
                              rowValid = false
                              errorMsg = 'The Is Student field must be either Y/N.'
                            }
                          }
                          break
                        case 5:
                          // Relationship
                          col = col.toUpperCase()
                          const relationshipValidation = await validator.verify(col, 'required')
                          if (!relationshipValidation.valid) {
                            rowValid = false
                            errorMsg = relationshipValidation.errors[0].replace('{field}', 'Relationship')
                          } else {
                            // Check valid values
                            if (col === 'S' || col === 'C') {
                              commitInsured.relationship = col
                            } else {
                              rowValid = false
                              errorMsg = 'The Relationship field must be either S/C.'
                            }
                          }
                          break
                      }
                    } catch (err) {
                      rowValid = false
                      errorMsg = 'Row Exception Error. Please check input fields.'
                    }
                    if (!rowValid) {
                      fullValid = false
                      break
                    }
                  }
                } else {
                  rowValid = false
                  errorMsg = 'Invalid Column No.'
                }
                if (rowValid) {
                  commitInsureds.push(commitInsured)
                } else {
                  this.errorMessage('Row ' + (i + 2) + ': ' + errorMsg)
                }
              }
              if (fullValid) {
                for (const commitInsured of commitInsureds) {
                  this.$store.commit(QUOTE_TRAVEL_INSURED_ADD, commitInsured)
                }
                this.$snotify.success('Insureds Uploaded')
              }
            }
            this.uploading = false
          }
          fileReader.readAsText(file)
        } else {
          this.$snotify.error('Invalid File Type. Only accepts CSV file.')
          this.uploading = false
        }
      } else {
        this.$snotify.error('Invalid File')
      }

      // Reset the value (v-model not supported), so that user can upload same file again
      this.$refs.uploadForm.reset()
    },
    downloadInsureds() {
      try {
        this.downloading = true
        let csvContent = ''
        if (this.globalForm.travelType === 'F') {
          const titleRow = ['Name', 'NricPassport', 'DoB (dd/MM/yyyy)', 'Gender (F/M), Full Time Student (Y/N), Relationship (S: Spouse | C: Child)']
          csvContent += titleRow.join(',') + '\r\n'
        } else {
          const titleRow = ['Name', 'NricPassport', 'DoB (dd/MM/yyyy)', 'Gender (F/M), Full Time Student (Y/N)']
          csvContent += titleRow.join(',') + '\r\n'
        }
        if (this.insureds && this.insureds.length > 0) {
          for (const insured of this.insureds) {
            let tempRow = []
            tempRow.push(insured.name)
            tempRow.push(insured.nricPassport)
            tempRow.push(formatDisplayDate(insured.dob))
            tempRow.push(insured.gender)
            tempRow.push(insured.isStudent ? 'Y' : 'N')
            if (this.globalForm.travelType === 'F') {
              if (insured.relationship) {
                tempRow.push(insured.relationship)
              } else {
                tempRow.push('')
              }
            }
            csvContent += tempRow.join(',') + '\r\n'
          }
        }

        // Convert to blob and prompt download
        const blobContent = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        downloadPrompt(blobContent, 'insureds.csv')
        this.downloading = false
      } catch (err) {
        console.log(err)
        this.downloading = false
      }
    },
    doBack() {
      this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 3)
      this.$router.push({ name: 'travelQuoteApplicant' })
    },
    doProceed() {
      this.resetMessages()
      // Need to validate no of insured
      if (Number(this.insuredCount.adult) === Number(this.globalForm.noAdult) && Number(this.insuredCount.child) === Number(this.globalForm.noChild)) {
        // Validate duplication of nricPassport and spouse count
        let spouse = 0
        let nricPassports = []
        let duplicateNricPassport = false
        for (const insured of this.insureds) {
          if (insured.relationship === 'S') {
            spouse++
          }
          if (nricPassports.includes(insured.nricPassport)) {
            duplicateNricPassport = true
            break
          } else {
            nricPassports.push(insured.nricPassport)
          }
        }
        if (spouse > 1) {
          this.promptFieldCheck()
          this.errorMessage('There is more than 1 Spouse in the application')
        } else if (duplicateNricPassport) {
          this.promptFieldCheck()
          this.errorMessage('There are duplicate NRIC/Passport No.')
        } else {
          this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 5)
          this.$router.push({ name: 'travelQuotePremium' })
        }
      } else {
        this.promptFieldCheck()
        this.errorMessage('Please check that the No. of Adult(s) and No. of Child(ren) matches your selection')
      }
    },
    async doPartialSave() {
      // No mandatory validation here. Commit whatever thats on screen and do neccesary actions before saving
      // Must return true/false
      try {
        await this.$store.dispatch('saveQuote')
        return true
      } catch (err) {
        return false
      }
    }
  },
  beforeMount() {
    // Check quote lifecycle (might be using back link or url)
    if (this.$store.state.agency.quotation.quoteLifeCycle !== 4) {
      this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 4)
    }
    this.doInit()
  },
  mixins: [
    MessageMixin,
    SaveQuoteMixin,
    TableConst.default
  ]
}
</script>

<style lang="scss">
input[type="file"] {
  position: absolute;
  left: -99999px;
}
</style>
